import React, { useEffect, useState, useRef } from 'react'
import restClient from 'Shared/hooks/restClient'
import Table from 'AnalyticsDashboard/components/Table'
import TimeSeriesTable from './TimeSeriesTable'
import PivotTable from './PivotTable'
import LineChart from 'AnalyticsDashboard/components/LineChart'
import LineChartV2 from 'AnalyticsDashboard/components/LineChartV2'
import PieChart from 'AnalyticsDashboard/components/PieChart'
import BarChart from 'AnalyticsDashboard/components/BarChart'
import ColumnChartV2 from 'AnalyticsDashboard/components/ColumnChartV2'
import PieChartV2 from 'AnalyticsDashboard/components/PieChartV2'
import GaugeChart from 'AnalyticsDashboard/components/GaugeChart'
import MapChart from 'AnalyticsDashboard/components/MapChart'
import NumberChart from 'AnalyticsDashboard/components/NumberChart'
import ConfidenceIntervalLineChart from 'AnalyticsDashboard/components/ConfidenceIntervalLineChart'
import prepareChartData from 'Analytics/utils/prepareChartData'
import Loading from 'Shared/components/Loading'
import { useAnalyticsDashboard, useAnalyticsQuery, useAnalyticsContext } from 'AnalyticsDashboard/AnalyticsContext'
import type { Metric, RawSqlFactTablePayload } from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

// Chart type display labels
const CHART_TYPE_LABELS: Record<string, string> = {
  table: 'Table',
  line: 'Line Chart',
  lineV2: 'Line Chart',
  pie: 'Pie Chart',
  bar: 'Bar Chart',
  columnV2: 'Column Chart',
  barV2: 'Bar Chart',
  pieV2: 'Pie Chart',
  map: 'Map (UK)',
  mapWorld: 'Map (World)',
  timeSeriesTable: 'Time Series Table',
  number: 'Number',
  gauge: 'Gauge',
  confidenceIntervalLineChart: 'Confidence Interval',
  pivotTable: 'Pivot Table',
  json: 'JSON'
}

interface AnalyticsQueryProps {
  chartType?: string
  supportedChartTypes?: string[]
  chartOptions?: Record<string, any>
  controlOptions?: { showChartType?: boolean; showTimeBucket?: boolean }
  metrics?: Metric[]
  dimensions?: Array<{ id: string; timeBucket?: string }>
  primaryDimension?: { id: string; label?: string }
  secondaryDimension?: { id: string; label?: string }
  parameters?: Record<string, any>
  global?: boolean
  orderBy?: Array<{ kind?: string; id: string; direction: string }>
  limit?: number | null
  overrideFilters?: Record<string, any>
  comparePeriods?: number | null
  chartComponent?: any
  viewMode?: 'chart' | 'table' | 'sql'
  rawSqlFactTable?: RawSqlFactTablePayload
  assistantConversationId?: number
}

export default function AnalyticsQuery({
  chartType = 'line',
  supportedChartTypes = [],
  chartOptions,
  controlOptions = { showChartType: false, showTimeBucket: true },
  metrics = [],
  dimensions = [],
  primaryDimension = { id: 'time', label: 'Time' },
  secondaryDimension,
  parameters = {},
  global = false,
  orderBy = [],
  limit = null,
  overrideFilters = {},
  comparePeriods = null,
  chartComponent = null,
  viewMode = 'chart',
  rawSqlFactTable,
  assistantConversationId
}: AnalyticsQueryProps) {
  const { registerQuery, unregisterQuery } = useAnalyticsDashboard()

  const idRef = useRef<string | null>(null)
  if (!idRef.current) idRef.current = _.uniqueId('aq_')
  const id = idRef.current

  useEffect(() => {
    registerQuery({
      id,
      chartType,
      supportedChartTypes,
      chartOptions,
      controlOptions,
      dimensions,
      primaryDimension,
      secondaryDimension,
      metrics,
      parameters,
      global,
      orderBy,
      limit,
      overrideFilters,
      comparePeriods,
      chartComponent,
      rawSqlFactTable,
      assistantConversationId
    })

    return () => unregisterQuery(id)
  }, [])

  return <AnalyticsQueryInner id={id} viewMode={viewMode} />
}

function AnalyticsQueryInner({ id, viewMode }: { id: string; viewMode: string }) {
  const { query, updateQuery, updateDimension } = useAnalyticsQuery(id)
  const { state: { uiEnabled } } = useAnalyticsContext()
  const { fetchData, requiredFiltersMet } = useAnalyticsDashboard()

  const queryExists = !!query
  const timeBucket = query?.dimensions?.find(d => d.id === 'time')?.timeBucket || null
  useEffect(() => {
    if (!queryExists) return
    fetchData(id)
  }, [queryExists, timeBucket, requiredFiltersMet])

  // For raw_sql tiles, fetch the backend-synthesised FactTable once on mount
  // so the slot UI / filter bar sees the real segment dimensions (incl.
  // dynamic per-client organisation segments) instead of the client-side
  // approximation. The result is cached on the QueryState; falling back to
  // the approximation is fine in the brief window before this returns.
  const rawSqlPayload = query?.rawSqlFactTable
  const hasSynth = !!query?.rawSqlSynthesizedFactTable
  useEffect(() => {
    if (!rawSqlPayload || hasSynth) return
    let cancelled = false
    restClient
      .post('/api/analytics/analytics_queries/validate_raw_sql', { sql: rawSqlPayload.sql })
      .then(({ data }) => {
        if (cancelled || !data?.valid || !data.factTable) return
        updateQuery({ rawSqlSynthesizedFactTable: data.factTable })
      })
      .catch(() => { /* fallback to client-side synthesis */ })
    return () => { cancelled = true }
    // Refetch only when the SQL changes (rawSqlPayload identity changes on every
    // render but the SQL string is the actual cache key here).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSqlPayload?.sql])

  const [activeTabId, setActiveTabId] = useState<string | null>(null)

  if (!requiredFiltersMet) {
    return <p className="analytics-query__placeholder">Select required filters to load data.</p>
  }

  if (!query || (query.dataLoading && !query.dataError)) {
    return <Loading />
  }

  if (query.dataError) {
    return <p className="analytics-query__error">{query.dataError}</p>
  }

  const { data: returnedData, dataLoading, chartType, supportedChartTypes = [], controlOptions, metrics: queryMetrics, chartOptions = {}, dimensions, chartComponent } = query
  const { showChartType, showTimeBucket } = controlOptions || {}
  const showChartTypeToggle = supportedChartTypes.length > 1

  // Handle dimension_info key casing — backend may return either form
  const { data, sql } = returnedData || {}
  const dimensionInfo = returnedData?.dimensionInfo || returnedData?.dimension_info || {}

  const effectiveChartOptions = chartOptions

  const primaryDimension = { ...query.primaryDimension, id: _.camelCase(query.primaryDimension?.id || 'time') }
  const secondaryDimension = query.secondaryDimension ? { ...query.secondaryDimension, id: _.camelCase(query.secondaryDimension.id) } : null

  // For v2 metrics, IDs are already in the right format (m1, m2, etc.)
  const metrics = (queryMetrics || []).map((metric: Metric) => ({ ...metric, id: metric.id }))
  const chartData = data ? prepareChartData({ chartType, data, metrics, primaryDimension, secondaryDimension }) : null

  const showData = data && data.length > 0 && !dataLoading
  const setChartType = (newChartType: string) => updateQuery({ chartType: newChartType })

  const chartProps = { data: chartData, chartOptions: effectiveChartOptions, dimensionInfo }
  const rawChartProps = { data, chartOptions: effectiveChartOptions, dimensionInfo, query }

  const effectiveChartType = viewMode === 'table' ? 'table' : chartType

  const renderChart = () => (
    <>
      {showChartTypeToggle && viewMode === 'chart' && (
        <div className='chart-type-toggle'>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            {supportedChartTypes.map(type => (
              <option key={type} value={type}>{CHART_TYPE_LABELS[type] || type}</option>
            ))}
          </select>
        </div>
      )}
      {uiEnabled && showTimeBucket && timeBucket && (
        <div className="time-bucket-toggle">
          {['hour', 'day', 'week', 'month', 'quarter', 'year'].map(b => (
            <button
              key={b}
              type="button"
              className={`time-bucket-toggle__btn ${timeBucket === b ? 'active' : ''}`}
              onClick={() => updateDimension('time', { timeBucket: b })}
            >
              {_.capitalize(b)}
            </button>
          ))}
        </div>
      )}
      {dataLoading && <Loading />}
      {!showData && !dataLoading && <p>No data available for the selected filters and segments.</p>}
      {showData && effectiveChartType === 'lineV2' && <LineChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'columnV2' && <ColumnChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'barV2' && <ColumnChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'pieV2' && <PieChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'map' && <MapChart {...rawChartProps} mapId={effectiveChartOptions.mapId || 'uk-wpc'} />}
      {showData && effectiveChartType === 'mapWorld' && <MapChart {...rawChartProps} mapId='world' />}
      {showData && effectiveChartType === 'table' && <Table {...rawChartProps} />}
      {showData && effectiveChartType === 'timeSeriesTable' && <TimeSeriesTable {...rawChartProps} />}
      {showData && effectiveChartType === 'number' && <NumberChart {...rawChartProps} />}
      {showData && effectiveChartType === 'gauge' && <GaugeChart {...rawChartProps} />}
      {showData && effectiveChartType === 'pivotTable' && <PivotTable {...rawChartProps} />}
      {showData && effectiveChartType === 'confidenceIntervalLineChart' && <ConfidenceIntervalLineChart {...rawChartProps} />}
      {showData && effectiveChartType === 'json' && <div>{ JSON.stringify(data, null, 2) }</div>}
      {showData && chartComponent && React.createElement(chartComponent, { data, chartOptions, query })}
    </>
  )

  return (
    <div className='analytics-query'>
      {viewMode === 'sql' ? (
        sql ? (
          <pre className="analytics-query__sql-content">{sql}</pre>
        ) : (
          <p className="analytics-query__sql-unavailable">SQL not available (dev mode only)</p>
        )
      ) : (
        renderChart()
      )}
    </div>
  )
}
